import { useState } from 'react';
import { LiveRibbon } from './LiveRibbon';
import { OutcomeChips } from './OutcomeChips';
import { formatPhone } from './format';
import { MaskaTelefona } from '../hodba/MaskaTelefona';
import { formatDuration } from './statusVocabulary';
import type { CallRow, OutcomeChip } from './callApi';
import type { LiveTranscript, WorkplaceStateHook } from './useWorkplaceState';

/**
 * Экран «Обработка вызова» (ТЗ §2.6, §2.13, §4.5).
 *
 * ⭐ Появился по жалобе владельца 13.08: «после завершения звонка кабинет
 * остаётся на экране этого звонка». Так и было — окно разговора висело до
 * следующего набора, потому что экран смотрел на состояние трубки в браузере
 * («звонок завершён» остаётся там навсегда), а не на статус рабочего места.
 *
 * По ТЗ §2.6 после отбоя человек проходит ровно один промежуточный экран:
 * «Обработка» с таймером и двумя действиями («Продлить» / «Завершить»), а по
 * её окончании кабинет сам возвращается в ожидание. Поэтому здесь собрано
 * ровно то, что нужно для заполнения итога, и ничего из контролов живого
 * разговора: кнопки «Завершить звонок», паузы и микрофона после отбоя не
 * значат ничего, а на экране они означают, что разговор ещё идёт.
 */
export function AfterCallPanel({
  workplace,
  lastCall,
  clientPhone,
  operatorName,
  transcript,
  outcomes,
  pickedOutcomeId,
  confirmDone,
  onPickOutcome,
  onConfirmHuman,
}: {
  workplace: WorkplaceStateHook;
  /** Строка только что состоявшегося разговора — из журнала, не из памяти вкладки. */
  lastCall: CallRow | null;
  clientPhone: string;
  operatorName: string;
  transcript: LiveTranscript | null;
  outcomes: OutcomeChip[];
  pickedOutcomeId: number | null;
  confirmDone: boolean;
  onPickOutcome: (outcome: OutcomeChip) => void;
  onConfirmHuman: (answer: 'confirmed_human' | 'was_machine') => Promise<void>;
}) {
  const phone = lastCall?.client_phone || clientPhone;
  const talkSec = lastCall?.talk_sec ?? null;

  return (
    <div data-tel="after-call">
      <ProcessingPanel workplace={workplace} />

      {lastCall?.outcome.needs_confirm && !confirmDone ? (
        <ConfirmHumanPanel onAnswer={onConfirmHuman} />
      ) : null}

      <div
        className="mt-3 rounded-ops-md p-4"
        style={{ background: 'var(--ops-surface)', border: '1px solid var(--ops-border)' }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            {/* zhur1: и после разговора номер — краями; раскрытие кликом. */}
            <div className="text-ops-h2 font-bold">
              {lastCall ? (
                <MaskaTelefona
                  masked={phone || null}
                  entityType="tel_calls"
                  entityId={lastCall.id}
                  bezZvonka
                  pusto="номер неизвестен"
                />
              ) : (
                formatPhone(phone) || 'номер неизвестен'
              )}
            </div>
            <div className="text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
              Разговор завершён
              {/*
                Длительность берётся у СЕРВЕРА, а не из таймера вкладки: именно
                это число попадёт в журнал и в сверку с движком (§6.1), и оно
                обязано совпасть с тем, что человек видит на экране.
              */}
              {talkSec !== null ? ` · ${formatDuration(talkSec)}` : ''}
            </div>
          </div>
        </div>

        {/* Лента разговора остаётся перед глазами: итог отмечают, глядя в текст. */}
        <LiveRibbon transcript={transcript} operatorName={operatorName} clientPhone={phone} />

        <OutcomeChips outcomes={outcomes} pickedOutcomeId={pickedOutcomeId} onPick={onPickOutcome} />
      </div>
    </div>
  );
}

/**
 * Плашка «Обработка вызова» (ТЗ §4.5, карта 2.4).
 *
 * Таймер и обе кнопки всегда вместе: владелец на приёмке 12.08 видел бегущий
 * таймер и ни одной кнопки — «просто таймер идёт». Ждать окончания срока не
 * работа, поэтому «Продлить» и «Завершить» стоят рядом с числом.
 */
export function ProcessingPanel({ workplace }: { workplace: WorkplaceStateHook }) {
  const { state, secondsLeft, busy } = workplace;

  if (state?.status !== 'processing') {
    return null;
  }

  return (
    <div
      data-tel="processing"
      className="rounded-ops-md p-4"
      style={{ background: 'var(--ops-surface)', border: '1px solid var(--ops-border)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <b className="text-ops-h2">Обработка вызова</b>
        <span className="flex items-center gap-2">
          <span className="tabular-nums text-ops-h2 font-bold">{formatDuration(secondsLeft ?? 0)}</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => void workplace.extend()}
            className="rounded-ops-sm px-3 py-1.5 text-ops-label"
            style={{ background: 'var(--ops-surface-2)', color: 'var(--ops-text)' }}
          >
            Продлить
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void workplace.finish()}
            className="rounded-ops-sm px-3 py-1.5 text-ops-label font-semibold"
            style={{ background: 'var(--ops-success-soft)', color: 'var(--ops-success)' }}
          >
            Завершить
          </button>
        </span>
      </div>
      <p className="mt-2 text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
        Заполните сделку и карточку в наших системах. По истечении срока статус сам вернётся в «Готов».
        Продлений за этот раз: {state.extensions}.
      </p>
    </div>
  );
}

/**
 * Вопрос о неоднозначном исходе (ТЗ §2.13, карта 2.6).
 *
 * Показывается ТОЛЬКО когда автоматика не смогла решить сама: разговор вышел
 * короче порога и вердикта автоответчика нет. При однозначном исходе вопроса
 * нет вовсе — лишний вопрос обесценивает все остальные.
 */
export function ConfirmHumanPanel({
  onAnswer,
}: {
  onAnswer: (answer: 'confirmed_human' | 'was_machine') => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  const answer = (value: 'confirmed_human' | 'was_machine') => {
    setBusy(true);
    void onAnswer(value).finally(() => setBusy(false));
  };

  return (
    <div
      data-tel="confirm-human"
      className="mt-3 flex flex-wrap items-center gap-3 rounded-ops-md p-3"
      style={{ background: 'var(--ops-warning-soft)', border: '1px solid var(--ops-warning)' }}
    >
      <b className="text-ops-label">Разговор вышел коротким — кто ответил?</b>

      <button
        type="button"
        disabled={busy}
        onClick={() => answer('confirmed_human')}
        className="rounded-ops-sm px-3 py-1.5 text-ops-label font-semibold disabled:opacity-50"
        style={{ background: 'var(--ops-success)', color: '#fff' }}
      >
        Человек
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={() => answer('was_machine')}
        className="rounded-ops-sm px-3 py-1.5 text-ops-label disabled:opacity-50"
        style={{ background: 'var(--ops-surface)', color: 'var(--ops-text)', border: '1px solid var(--ops-border)' }}
      >
        Автоответчик
      </button>
    </div>
  );
}
